import jsPDF from 'jspdf'
import JsBarcode from 'jsbarcode'
import QRCode from 'qrcode'

const DPI = 203 // Rollo thermal printer native resolution
const MM_PER_INCH = 25.4

interface PrintZone {
  id: string
  zone_type: 'text' | 'qr' | 'barcode' | 'image' | 'line' | 'box'
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  content_rotation?: number
  field_mapping?: string
  static_content?: string
  font_family?: string
  font_size?: number
  font_weight?: string
  text_align?: string
  text_color?: string
  bg_color?: string
  border_width?: number
  border_color?: string
  barcode_type?: string
  qr_error_correction?: string
  visible?: boolean
}

interface PrintTemplate {
  id: number
  name: string
  width: number
  height: number
  orientation?: 'portrait' | 'landscape'
  scenario: string
  zones: PrintZone[]
}

interface EntityData {
  [key: string]: any
}

/**
 * Generate a label PDF from template and data
 */
export async function generateLabelPDF(
  template: PrintTemplate,
  data: EntityData,
  quantity: number = 1
): Promise<jsPDF> {
  const widthMM = template.width * MM_PER_INCH
  const heightMM = template.height * MM_PER_INCH

  // jsPDF orientation logic:
  // - When using custom format array [w, h], jsPDF uses those EXACT dimensions
  // - orientation parameter 'l' or 'p' tells jsPDF which edge is "up"
  // - For landscape labels, we want width > height and orientation 'l'
  const orientation = widthMM > heightMM ? 'l' : 'p'

  const pdf = new jsPDF({
    unit: 'mm',
    orientation: orientation,
    format: [widthMM, heightMM],
    compress: true
  })

  // Generate labels for specified quantity
  for (let copy = 0; copy < quantity; copy++) {
    if (copy > 0) {
      pdf.addPage([widthMM, heightMM], orientation)
    }

    // Render each zone
    for (const zone of template.zones) {
      if (zone.visible === false) continue

      await renderZone(pdf, zone, data, template.width, template.height)
    }
  }

  return pdf
}

/**
 * Render a single zone on the PDF
 */
async function renderZone(
  pdf: jsPDF,
  zone: PrintZone,
  data: EntityData,
  templateWidth: number,
  templateHeight: number
): Promise<void> {
  const xMM = zone.x * MM_PER_INCH
  const yMM = zone.y * MM_PER_INCH
  const wMM = zone.width * MM_PER_INCH
  const hMM = zone.height * MM_PER_INCH

  // Get content value
  const content = zone.field_mapping
    ? (data[zone.field_mapping] || zone.static_content || '')
    : (zone.static_content || '')

  switch (zone.zone_type) {
    case 'text':
      renderTextZone(pdf, zone, content, xMM, yMM, wMM, hMM)
      break

    case 'qr':
      await renderQRZone(pdf, zone, content, xMM, yMM, wMM, hMM)
      break

    case 'barcode':
      await renderBarcodeZone(pdf, zone, content, xMM, yMM, wMM, hMM)
      break

    case 'line':
      renderLineZone(pdf, zone, xMM, yMM, wMM, hMM)
      break

    case 'box':
      renderBoxZone(pdf, zone, xMM, yMM, wMM, hMM)
      break

    case 'image':
      // Image rendering would require actual image data
      // For now, render a placeholder box
      renderBoxZone(pdf, zone, xMM, yMM, wMM, hMM)
      break
  }
}

/**
 * Render text zone
 */
function renderTextZone(
  pdf: jsPDF,
  zone: PrintZone,
  content: string,
  xMM: number,
  yMM: number,
  wMM: number,
  hMM: number
): void {
  // Background color
  if (zone.bg_color) {
    pdf.setFillColor(zone.bg_color)
    pdf.rect(xMM, yMM, wMM, hMM, 'F')
  }

  // Border
  if (zone.border_width && zone.border_width > 0) {
    pdf.setLineWidth(zone.border_width * 0.352778) // px to mm
    pdf.setDrawColor(zone.border_color || '#000000')
    pdf.rect(xMM, yMM, wMM, hMM, 'S')
  }

  // Text
  pdf.setFont(zone.font_family || 'helvetica', zone.font_weight || 'normal')
  pdf.setFontSize(zone.font_size || 12)
  pdf.setTextColor(zone.text_color || '#000000')

  // Calculate text position based on alignment
  const textY = yMM + (hMM / 2) + (zone.font_size || 12) * 0.352778 / 2

  let textX = xMM
  let align: 'left' | 'center' | 'right' = 'left'

  switch (zone.text_align) {
    case 'center':
      textX = xMM + wMM / 2
      align = 'center'
      break
    case 'right':
      textX = xMM + wMM
      align = 'right'
      break
    default:
      textX = xMM + 2
      align = 'left'
  }

  pdf.text(String(content), textX, textY, { align, maxWidth: wMM - 4 })
}

/**
 * Render QR code zone
 */
async function renderQRZone(
  pdf: jsPDF,
  zone: PrintZone,
  content: string,
  xMM: number,
  yMM: number,
  wMM: number,
  hMM: number
): Promise<void> {
  if (!content) return

  try {
    const qrDataURL = await QRCode.toDataURL(String(content), {
      errorCorrectionLevel: zone.qr_error_correction || 'M',
      margin: 1,
      width: wMM * 10 // Higher resolution
    })

    pdf.addImage(qrDataURL, 'PNG', xMM, yMM, wMM, hMM)
  } catch (error) {
    console.error('QR code generation error:', error)
    // Fallback: render placeholder box
    pdf.setDrawColor('#000000')
    pdf.rect(xMM, yMM, wMM, hMM, 'S')
    pdf.setFontSize(8)
    pdf.text('QR', xMM + wMM / 2, yMM + hMM / 2, { align: 'center' })
  }
}

/**
 * Render barcode zone
 */
async function renderBarcodeZone(
  pdf: jsPDF,
  zone: PrintZone,
  content: string,
  xMM: number,
  yMM: number,
  wMM: number,
  hMM: number
): Promise<void> {
  if (!content) return

  try {
    const canvas = document.createElement('canvas')

    JsBarcode(canvas, String(content), {
      format: zone.barcode_type || 'CODE128',
      width: 2,
      height: hMM * 3.779528, // mm to px at 96 DPI
      displayValue: false,
      margin: 0
    })

    const barcodeDataURL = canvas.toDataURL('image/png')
    pdf.addImage(barcodeDataURL, 'PNG', xMM, yMM, wMM, hMM)
  } catch (error) {
    console.error('Barcode generation error:', error)
    // Fallback: render placeholder
    pdf.setDrawColor('#000000')
    pdf.rect(xMM, yMM, wMM, hMM, 'S')
    pdf.setFontSize(8)
    pdf.text('BARCODE', xMM + wMM / 2, yMM + hMM / 2, { align: 'center' })
  }
}

/**
 * Render line zone
 */
function renderLineZone(
  pdf: jsPDF,
  zone: PrintZone,
  xMM: number,
  yMM: number,
  wMM: number,
  hMM: number
): void {
  pdf.setLineWidth(zone.border_width || 1)
  pdf.setDrawColor(zone.border_color || '#000000')
  pdf.line(xMM, yMM, xMM + wMM, yMM + hMM)
}

/**
 * Render box/rectangle zone
 */
function renderBoxZone(
  pdf: jsPDF,
  zone: PrintZone,
  xMM: number,
  yMM: number,
  wMM: number,
  hMM: number
): void {
  // Fill
  if (zone.bg_color) {
    pdf.setFillColor(zone.bg_color)
    pdf.rect(xMM, yMM, wMM, hMM, 'F')
  }

  // Border
  if (zone.border_width && zone.border_width > 0) {
    pdf.setLineWidth(zone.border_width * 0.352778) // px to mm
    pdf.setDrawColor(zone.border_color || '#000000')
    pdf.rect(xMM, yMM, wMM, hMM, 'S')
  }
}

/**
 * Map entity data to template format
 */
export function mapEntityToTemplateData(
  entityType: string,
  entity: any
): EntityData {
  const mappings: { [key: string]: (e: any) => EntityData } = {
    item: (item) => ({
      item_name: item.name || item.title || '',
      ic: item.ic || item.sku || '',
      category: item.category_name || '',
      brand: item.brand || '',
      price: item.target_price ? `$${Number(item.target_price).toFixed(2)}` : '',
      size: item.size || '',
      color: item.color || '',
      condition: item.condition || '',
      description: item.description || ''
    }),

    location: (loc) => ({
      location_name: loc.name || '',
      location_code: loc.location_code || '',
      zone: loc.zone || '',
      capacity: loc.capacity ? `${loc.capacity} units` : ''
    }),

    shelf: (shelf) => ({
      shelf_name: shelf.name || '',
      shelf_code: shelf.shelf_code || '',
      location_name: shelf.location_name || '',
      tier_count: shelf.tiers ? `${shelf.tiers} tiers` : '',
      capacity: shelf.capacity ? `${shelf.capacity} units` : ''
    }),

    tote: (tote) => ({
      tote_name: tote.name || '',
      tote_code: tote.tote_code || '',
      location_name: tote.location_name || '',
      capacity: tote.capacity ? `${tote.capacity} items` : ''
    })
  }

  const mapper = mappings[entityType]
  return mapper ? mapper(entity) : {}
}

/**
 * Open PDF in browser print dialog
 */
export function printPDF(pdf: jsPDF): void {
  // Open in new window with print dialog
  const blob = pdf.output('blob')
  const url = URL.createObjectURL(blob)

  const printWindow = window.open(url, '_blank')
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print()
    }
  }
}

/**
 * Download PDF
 */
export function downloadPDF(pdf: jsPDF, filename: string): void {
  pdf.save(filename)
}
